import React from 'react';
import {
  Bell, BellOff, BellRing, CheckCircle2, AlertTriangle, Loader2, Send, Trash2, Smartphone,
} from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { relative, absolute } from './SchwabConnection';

/**
 * Settings card for browser push: turn this device on/off, send a test, see
 * and prune the other subscribed devices, and choose which kinds of
 * notification go out.
 *
 * The device on/off is immediate (it's a browser permission + backend row).
 * The per-kind switches are ordinary settings, saved with "Save Settings",
 * so they come in as props from the Settings page.
 *
 * Props:
 *   notifications  -- settings.notifications ({ [kind]: { push: bool } })
 *   onChange(next) -- replace settings.notifications
 */
const PushNotifications = ({ notifications, onChange }) => {
  const {
    support, permission, config, devices, thisDevice, loading, busy, error,
    testResult, enable, disable, removeDevice, sendTest,
  } = usePushNotifications();

  const pushOn = (kind) => notifications?.[kind]?.push !== false;
  const setPushOn = (kind, on) => onChange({
    ...(notifications || {}),
    [kind]: { ...(notifications?.[kind] || {}), push: on },
  });

  const header = (
    <div className="card-header">
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Bell size={20} />
        Push Notifications
      </h2>
    </div>
  );

  if (loading) {
    return (
      <div className="card settings-card">
        {header}
        <div className="settings-form">
          <p className="settings-hint">Checking notification status…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card settings-card">
      {header}

      <div className="settings-form">
        {config && !config.configured && (
          <p className="settings-hint" style={{ color: 'var(--warning-color)' }}>
            <AlertTriangle size={14} />
            Push isn't set up on the server yet: the VAPID keys are missing. See NOTIFICATIONS.md.
          </p>
        )}

        <ThisDeviceStatus
          support={support}
          permission={permission}
          subscribed={Boolean(thisDevice)}
        />

        {error && (
          <p className="settings-hint" style={{ color: 'var(--danger-color)' }}>{error}</p>
        )}

        {testResult && <TestResultLine result={testResult} />}

        <div className="schwab-actions">
          {support.supported && config?.configured && permission !== 'denied' && (
            thisDevice ? (
              <button type="button" className="btn btn-secondary" onClick={disable} disabled={busy}>
                {busy ? <Loader2 size={16} className="spin" /> : <BellOff size={16} />}
                Turn off on this device
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={enable} disabled={busy}>
                {busy ? <Loader2 size={16} className="spin" /> : <BellRing size={16} />}
                Turn on for this device
              </button>
            )
          )}

          {devices.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={sendTest} disabled={busy}>
              <Send size={16} />
              Send test
            </button>
          )}
        </div>

        <div className="settings-field" style={{ marginTop: '0.5rem' }}>
          <label className="settings-label">Notify me when</label>
          <div className="settings-checkbox">
            <input
              type="checkbox"
              id="push-daily-report"
              checked={pushOn('daily-report')}
              onChange={(e) => setPushOn('daily-report', e.target.checked)}
            />
            <label htmlFor="push-daily-report">The daily report is ready</label>
          </div>
          <div className="settings-checkbox">
            <input
              type="checkbox"
              id="push-schwab-token"
              checked={pushOn('schwab-token')}
              onChange={(e) => setPushOn('schwab-token', e.target.checked)}
            />
            <label htmlFor="push-schwab-token">
              The Schwab login is expiring (daily from day 5) or sync has stopped
            </label>
          </div>
          <p className="settings-hint">
            Applies to every device. Saved with "Save Settings" below.
          </p>
        </div>

        {devices.length > 0 && (
          <div className="settings-field">
            <label className="settings-label">Devices receiving push</label>
            <div className="schwab-accounts">
              {devices.map((d) => (
                <div key={d.id} className="schwab-account-row">
                  <span className="schwab-account-name" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Smartphone size={14} />
                    {d.label || 'Unnamed device'}
                    {thisDevice?.id === d.id && (
                      <span style={{ color: 'var(--success-color)', fontWeight: 500 }}>· this device</span>
                    )}
                  </span>
                  <span className="schwab-account-detail" title={absolute(d.lastSuccessAt)}>
                    {d.lastSuccessAt ? `last delivered ${relative(d.lastSuccessAt)}` : 'nothing delivered yet'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => removeDevice(d.id)}
                    disabled={busy}
                    title="Stop sending to this device"
                    aria-label={`Remove ${d.label || 'device'}`}
                    style={{ padding: '0.25rem 0.5rem' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** One line saying whether this browser gets pushes, and if not, why not. */
const ThisDeviceStatus = ({ support, permission, subscribed }) => {
  if (support.needsInstall) {
    return (
      <p className="settings-hint" style={{ marginTop: 0 }}>
        <Smartphone size={14} />
        On iPhone, push only works in the home-screen app. In Safari tap Share, then
        "Add to Home Screen", open RussAdvisor from that icon, and come back here.
      </p>
    );
  }

  if (!support.supported) {
    return (
      <p className="settings-hint" style={{ marginTop: 0 }}>
        This browser doesn't support push notifications.
      </p>
    );
  }

  if (permission === 'denied') {
    return (
      <p className="settings-hint" style={{ marginTop: 0, color: 'var(--danger-color)' }}>
        <BellOff size={14} />
        Notifications are blocked for this site. Allow them in the browser (or iOS
        Settings → Notifications → RussAdvisor), then reload.
      </p>
    );
  }

  return subscribed ? (
    <div className="schwab-status-line" style={{ color: 'var(--success-color)' }}>
      <CheckCircle2 size={18} />
      <strong>On for this device</strong>
    </div>
  ) : (
    <div className="schwab-status-line" style={{ color: 'var(--text-secondary)' }}>
      <BellOff size={18} />
      <strong>Off for this device</strong>
    </div>
  );
};

const TestResultLine = ({ result }) => {
  if (result.skippedReason) {
    return (
      <p className="settings-hint" style={{ color: 'var(--warning-color)' }}>
        Not sent: {result.skippedReason}.
      </p>
    );
  }

  const parts = [`Sent to ${result.delivered} device${result.delivered === 1 ? '' : 's'}`];
  if (result.failed) parts.push(`${result.failed} failed`);
  if (result.removed) parts.push(`${result.removed} expired and removed`);

  return (
    <p
      className="settings-hint"
      style={{ color: result.failed ? 'var(--warning-color)' : 'var(--success-color)' }}
    >
      {parts.join(', ')}.
    </p>
  );
};

export default PushNotifications;
